import * as React from 'react';
import {
  Container,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Link,
  Button,
  Grid,
  Box,
  ListItem,
  List,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CustomHeader from '../components/CustomHeader';

// --- DATA CONFIGURATION ---

const SUBMISSION_TOPICS = [
  'Data Science',
  'Computer Vision',
  'Natural Language Processing',
  'Machine Learning',
  'Time Series Analysis',
  'Forecasting',
  'Generative AI',
  'Neural Networks',
  'Explainable Artificial Intelligence',
  'Deep Learning',
  'Self-Supervised Learning',
  'Active Learning',
  'Artificial Intelligence',
  'Causal Machine Learning',
  'Multi-Modal Architectures',
];

const TRACKS = [
  {
    title: 'Track 1: Applied AI & Data Science in Industry',
    items: [
      'AI and data science solutions addressing real industry problems',
      'Deployed systems, pilots, prototypes, or well-motivated experimental studies',
      'Case studies, system architectures, and lessons learned',
      'Accepts industry–academia collaborative work',
    ],
  },
  {
    title: 'Track 2: AI for Societal Impact & Sustainable Development',
    items: [
      'AI applications in education, healthcare, agriculture, climate, governance, and social good',
      'Solutions for low-resource, multilingual, or infrastructure-constrained settings',
      'Responsible, inclusive, and trustworthy AI in practice',
      'Prototype systems and experimental evaluations are welcome',
    ],
  },
  {
    title: 'Track 3: Data Systems, MLOps & AI Infrastructure',
    items: [
      'Data engineering, data quality, data integration, and pipelines',
      'MLOps, deployment strategies, monitoring, and lifecycle management',
      'Scalable, cost-efficient, and edge AI systems',
      'Security, privacy, and governance in real-world AI systems',
    ],
  },
  {
    title: 'Track 4: Methods and Systems for Real-World AI Applications',
    items: [
      'Machine learning, deep learning, computer vision, NLP, and related methods',
      'Methodological contributions motivated by real-world problems',
      'Evaluation using real, public, synthetic, or simulated datasets',
      'Emphasis on applicability, robustness, and practical relevance',
    ],
  },
  {
    title: 'Track 5: Postgraduate Research (MSc / MPhil / PhD)',
    items: [
      'Early to mid-stage postgraduate research-in-progress',
      'Clear problem formulation, methodology, and experimental design',
      'Novel ideas, preliminary results, or partial systems',
      'Feedback-oriented review with oral or poster presentations',
      'Include work that does not fall under other tracks',
    ],
  },
  {
    title: 'Track 6: Undergraduate Research & Innovation',
    items: [
      'High-quality undergraduate final-year or capstone projects',
      'Applied research, prototypes, or proof-of-concept systems',
      'Strong problem definition, implementation, and evaluation',
      'Poster or demo-focused presentations with mentoring-style feedback',
      'Include work that does not fall under other tracks',
    ],
  },
];

const AUTHOR_INFO_SECTIONS = [
  {
    title: 'Paper Submission [CLOSED]',
    intro: 'Authors are invited to submit papers adhering to the following guidelines',
    type: 'blocks',
    blocks: [
      {
        title: 'Submission Requirements',
        content: [
          'Submissions for Tracks 1 - 4 must be original papers of 5 - 8 pages, including results, figures, tables, and references on their ongoing or completed research or commercial applications. Tracks 5 and 6 (Postgraduate and Undergraduate Research) will be accepting short papers of up to 4 pages.',
          'Papers should be submitted electronically in PDF format, strictly adhering to the ADScAI Publication Template.',
        ],
        button: { label: 'View Template', href: 'https://www.overleaf.com/read/trzntvmmfstg#ef8403', color: 'primary' },
        footer: 'Submissions that exceed page limits or violate the guidelines will not be considered.',
      },
      {
        title: 'Blind Review Policy',
        content: [
          'Submissions must not contain any identifying information such as author names, affiliations, or acknowledgements during the initial submission phase to ensure an unbiased review process.',
        ],
      },
      {
        title: 'Submission Portal & Template',
        content: [
          'All papers must be submitted exclusively via the conference\'s submission portal.',
        ],
        button: { label: 'Submission Portal Link', href: 'https://openreview.net/group?id=ADScAI/2026/Conference', color: 'secondary', disabled: true },
        footer: 'Authors without an existing OpenReview account are required to create one prior to submission. Detailed instructions can be found here:',
        link: {
          text: 'Creating an OpenReview Profile',
          href: 'https://docs.openreview.net/getting-started/creating-an-openreview-profile/signing-up-for-openreview',
        },
      },
    ],
  },
  {
    title: 'Poster and Oral Presentation Guidelines',
    tag: 'NEW',
    type: 'blocks',
    blocks: [
      {
        title: 'Oral Presentation Guidelines',
        type: 'list',
        items: [
          '1. Presentation must not exceed 10 mins',
          '2. The first bell will ring at 8 mins',
          '3. The second bell will ring at 10 mins',
          '4. Author must stop stop presenting at 10 mins',
          '5. Both slides and video must be submitted',
        ],
      },
      {
        title: 'Video Guidelines',
        type: 'list',
        items: [
          '1. Presentation recording should not exceed 10mins.',
          '2. Accepted video formats: MP4.',
          '3. Accepted maximum video file size: 100 MB.',
          '4. You are free to use any recording software such as OBS Studio, Zoom, Bandicam, Screen Recorder etc. to record your video. If your recording exceeds the maximum video file size, you should use a compression tool/software (Eg: Handbrake) to compress the video.',
          '5. Presentation Submission Deadline: Monday, April 27th at 11:59 PM IST.',
          {
            text: '6. Presentation (Slides & Video) Submission Link:',
            link: {
              text: 'Presentation Submission Link',
              href: 'https://forms.gle/QFdBjpRhm7UhCgWC9',
            },
          },
        ],
      },
      {
        title: 'Poster Presentation Guidelines',
        type: 'list',
        items: [
          {
            text: '1. All posters must follow the official',
            link: {
              text: 'ADScAI poster template',
              href: 'https://drive.google.com/drive/folders/1MVZmw2MghW8btRinQk5pUkUNSJuuTmv3?usp=drive_link',
            },
          },
          '2. Posters Submission Deadline: Sunday, April 26th at 11:59 PM IST',
          {
            text: '3. Poster Submission Link:',
            link: {
              text: 'Poster Submission Link',
              href: 'https://forms.gle/3N4x7BbxrYKyjpLi8',
            },
          },
        ],
      },
    ],
  },
  {
    title: 'Conference Registration Details',
    tag: 'NEW',
    intro: {
      text: 'Registrations for ADScAI are now open. ',
      link: {
        text: 'Click here',
        href: 'https://forms.gle/WFzDNBjC6uM153HMA'
      },
      suffix: ' to register. Note the registration fees below.'
    },
    type: 'list',
    items: [
      '• Non-Student Author (1 Paper): Rs 7,500 (Early Bird Registration)',
      '• Non-Student Co-Author (1 Paper): Rs 5,500  (Early Bird Registration)',
      '• Student Author (1 Paper): Rs 5,500  (Early Bird Registration)',
      '• Student Co-Author (1 Paper): Rs 4,500  (Early Bird Registration)',
      '• Non-Student Author (1 Paper): Rs 8,000',
      '• Non-Student Co-Author (1 Paper): Rs 7,500',
      '• Student Author (1 Paper): Rs 6,000',
      '• Student Co-Author (1 Paper): Rs 5,000',
      '• Additional Paper Registration (For Non-Student): Rs 1,000',
      '• Additional Paper Registration (For Student): Rs 500 ',
      'Note: Early Bird Registration Prices are only valid for payments made on or before 25.04.2026'
    ],
    faq: [
      {
        q: 'My paper has been accepted. Do I have to register for the conference for my paper to be published?',
        a: 'Yes. At least one author must register for ADScAI 2026 for the paper to be published in the proceedings, even though it has been accepted. Acceptance does not guarantee publication unless at least one author is present at the conference'
      },
      {
        q: 'My paper has multiple co-authors. Do all of them have to register?',
        a: 'We encourage all co-authors to register. However, each co-author registering is not a requirement, but at least one author must register for the conference for the paper to be published. Co-authors have a discounted registration fee.'
      },
      {
        q: 'My paper was accepted. Can I take part in the pre-conference workshops free of charge?',
        a: 'Yes, as an author of accepted papers, you are entitled to take part in the pre-conference workshops. You can register here:',
        link: {
          text: 'Workshop Registration Form',
          href: 'https://forms.gle/j8DwcJjsYFXNsPsf9'
        }
      },
      {
        q: 'Multiple co-authors of the paper have registered for the conference. Do all of them have to pay the registration fee?',
        a: 'Yes. The registration fee must be paid for each of the registering authors. The price slightly differs depending on whether you are the presenting author or a co-author. Payment confirmation of all presenting author and co-authors must be combined into a single document/image and uploaded along with the registration form.'
      },
      {
        q: 'My paper is accepted for a poster presentation. Would that be included in the proceedings?',
        a: ' Yes. All accepted papers will be included in the proceedings if a presenting author registers and presents it during the conference.'
      }
    ]
  },
  {
    title: 'Author Responsibilities',
    intro: 'Authors submitting work to ADScAI 2026 must adhere to the following ethical and professional standards:',
    type: 'list',
    items: [
      '1. The submission must represent original research conducted by the authors and must not infringe upon the intellectual property rights of any third party.',
      '2. Submitted content, including text, graphics, figures, and tables, must not have been previously published or under consideration elsewhere unless appropriate permissions have been obtained and properly cited.',
      '3. The submitted work must not be simultaneously under review at any other conference, journal, or publication platform.',
      '4. The list of authors must accurately reflect individuals who have made substantive intellectual contributions to the research.',
      '5. All data sources, images, graphics, figures, and other referenced material must be appropriately acknowledged and cited.',
      {
        text: '6. Authors must ensure compliance with the submission guidelines and publication ethics as outlined in the ACM Publication Policies and Procedures, available here:',
        link: {
          text: 'ACM Publications Policies and Procedures',
          href: 'https://www.acm.org/publications/policies/toc',
        },
      },
    ],
  },
  {
    title: 'Review Process',
    intro: 'Submissions will undergo a rigorous double-blind peer-review process:',
    type: 'list',
    items: [
      '1. Initial submissions with a high similarity index will be desk-rejected.',
      '2. Program Committee members will review submissions based on research expertise and relevance.',
      '3. Authors may be given a brief period to address initial review comments and submit a revised version.',
      '4. Final decisions will be made by the program chairs.',
    ],
  },
  {
    title: 'Ethics and Conduct',
    type: 'text',
    content: 'Authors are expected to adhere to the highest standards of research integrity. Any form of academic misconduct, including plagiarism, data fabrication, or falsification, will result in immediate rejection of the submission and potential reporting to the authors\' institutions.',
  },
  {
    title: 'Generative AI Policy',
    type: 'list',
    items: [
      'Authors must take full responsibility for all content submitted. Generative AI tools and technologies may not be listed as authors of a submission, and any use of generative AI tools and technologies to create content should instead be fully disclosed in the Acknowledgements section - for instance, “Section 3 was written with inputs from ChatGPT.”',
      'Generative AI assistance with the language of the paper need not be disclosed. This includes paraphrasing, grammar checkers, spell checkers and synonym tools.',
      'Automatic generation of text on pre-existing ideas must be accompanied by relevant citations and author(s) should specify where such automatically generated text is present.',
      'Authors should disclose the use of generative AI models when presenting ideas that were initially generated by these models but further developed by the authors.',
    ],
  },
  {
    title: 'Copyright/Publication Policy',
    type: 'list',
    items: [
      '1. By submitting an paper, the authors agree to grant ADScAI 2026 the non-exclusive right to publish the accepted papers.',
      '2. Accepted papers will be published online with a DOI, hosted on the University of Moratuwa Library website.',
      '3. At least one author of each accepted papers must register and present their work at ADScAI 2026.',
      '4. Requests to withdraw or modify an accepted paper are strongly discouraged and will be reviewed on a case-by-case basis.',
    ],
  },
  {
    title: 'More Information',
    type: 'contact',
    intro: 'For further inquiries, please reach out to the conference organizing committee via email:',
    email: 'adscai@cse.mrt.ac.lk',
  },
];

const PAGE_CONTENT = [
  {
    title: 'Submission Topics',
    type: 'topics',
    intro: 'The ADScAI 2026 Conference invites the submission of papers presenting original research contributions under the following themes:',
    data: SUBMISSION_TOPICS,
  },
  {
    title: 'Tracks',
    type: 'tracks',
    intro: 'ADScAI 2026 features multiple tracks to accommodate diverse research contributions and experience levels:',
    data: TRACKS,
  },
  {
    title: 'Information for Authors',
    type: 'authorInfo',
    data: AUTHOR_INFO_SECTIONS,
  },
];

// --- COMPONENTS ---

const Guidelines = () => {
  const renderListItem = (item, idx, sectionTitle = "") => {
    if (typeof item === 'string') {
      if (sectionTitle === "Conference Registration Details") {

        const addSpacing =
            item.endsWith('Non-Student Author (1 Paper): Rs 8,000') ||
            item.endsWith('Additional Paper Registration (For Non-Student): Rs 1,000');

        if (item.startsWith('Note:')) {
          return (
              <ListItem key={idx} sx={{mt: 2}}>
                <Typography variant='body1' fontWeight="bold">
                  {item}
                </Typography>
              </ListItem>
          );
        }

        return (
            <ListItem key={idx} sx={{mt: addSpacing ? 2 : 0}}>
              <Typography variant='body1'>
                {item}
              </Typography>
            </ListItem>
        );
      }
      const isNumbered = /^\d+\./.test(item);
      const text = isNumbered || item.startsWith('•') ? item : `• ${item}`;
      return (
        <ListItem key={idx}>
          <Typography variant='body1' color='textPrimary'>
            {text}
          </Typography>
        </ListItem>
      );
    }
    return (
      <ListItem key={idx}>
        <Typography variant='body1' color='textPrimary'>
          {item.text}{' '}
          {item.link && (
            <Link
              href={item.link.href}
              target='_blank'
              color='primary'
              sx={{
                textDecoration: 'underline',
                '&:hover': { color: 'secondary.main' },
              }}
            >
              {item.link.text}
            </Link>
          )}
        </Typography>
      </ListItem>
    );
  };

  const renderSectionContent = (section) => {
    switch (section.type) {
      case 'topics':
        return (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
              gap: 2,
              mt: 3,
            }}
          >
            {section.data.map((topic, index) => (
              <Box
                key={index}
                sx={{
                  background: 'white',
                  color: 'Black',
                  padding: '12px 16px',
                  borderRadius: '12px',
                  textAlign: 'center',
                  fontWeight: 'bold',
                  fontSize: '1.1rem',
                  boxShadow: '0 4px 10px rgba(0, 0, 0, 0.2)',
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  '&:hover': {
                    transform: 'scale(1.05)',
                    boxShadow: '0 6px 15px rgba(0, 0, 0, 0.3)',
                  },
                }}
              >
                {topic}
              </Box>
            ))}
          </Box>
        );

      case 'tracks':
        return (
          <Box sx={{ mt: 3 }}>
            {section.data.map((track, index) => (
              <Accordion key={index} sx={{ boxShadow: 2, borderRadius: 2, marginBottom: '10px' }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant='h6' fontWeight='bold' color='textPrimary'>
                    {track.title}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ backgroundColor: 'background.paper', padding: 3 }}>
                  <List sx={{ paddingLeft: 2 }}>
                    {track.items.map((item, idx) => renderListItem(item, idx))}
                  </List>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        );

      case 'authorInfo':
        return (
          <Box>
            {section.data.map((infoSection, index) => (
              <Accordion key={index} sx={{ boxShadow: 3, borderRadius: 2, marginBottom: '10px' }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant='h6' fontWeight='bold'>
                    {infoSection.title}{' '}
                    {infoSection.tag && (
                        <Box component="span" sx={{ color: 'red', fontWeight: 'bold' }}>
                          [{infoSection.tag}]
                        </Box>
                    )}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ backgroundColor: 'background.paper', padding: 3 }}>
                  {infoSection.intro && (
                      <Typography variant='body1' color='textSecondary' paragraph>
                        {typeof infoSection.intro === 'string' ? (
                            infoSection.intro
                        ) : (
                            <>
                              {infoSection.intro.text}
                              <Link
                                  href={infoSection.intro.link.href}
                                  target="_blank"
                                  color="primary"
                                  sx={{
                                    textDecoration: 'underline',
                                    '&:hover': { color: 'secondary.main' },
                                  }}
                              >
                                {infoSection.intro.link.text}
                              </Link>
                              {infoSection.intro.suffix}
                            </>
                        )}
                      </Typography>
                  )}


                  {infoSection.type === 'blocks' && (
                    <Box>
                      {infoSection.type === 'blocks' && (
                          <Box>
                            {infoSection.blocks.map((block, idx) => renderBlock(block, idx))}
                          </Box>
                      )}
                    </Box>
                  )}

                  {infoSection.type === 'list' && (
                    <List sx={{ paddingLeft: 2 }}>
                      {infoSection.items.map((item, idx) =>
                          renderListItem(item, idx, infoSection.title)
                      )}
                    </List>
                  )}

                  {infoSection.type === 'text' && (
                    <Typography variant='body1' color='textPrimary'>
                      {infoSection.content}
                    </Typography>
                  )}

                  {infoSection.type === 'contact' && (
                    <Typography variant='body1' color='textPrimary'>
                      Email:{' '}
                      <Link href={`mailto:${infoSection.email}`} color='primary'>
                        {infoSection.email}
                      </Link>
                    </Typography>
                  )}

                  {infoSection.faq && (
                      <Box sx={{ mt: 4 }}>
                        <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                          Frequently Asked Questions
                        </Typography>

                        {infoSection.faq.map((item, idx) => (
                            <Accordion key={idx} sx={{ boxShadow: 1, mb: 1 }}>
                              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography fontWeight="bold">
                                  {item.q}
                                </Typography>
                              </AccordionSummary>

                              <AccordionDetails>
                                <Typography variant="body1" sx={{ textAlign: 'left' }}>
                                  {item.a}{' '}

                                  {item.link && (
                                      <Link
                                          href={item.link.href}
                                          target="_blank"
                                          color="primary"
                                          sx={{
                                            textDecoration: 'underline',
                                            '&:hover': { color: 'secondary.main' },
                                          }}
                                      >
                                        {item.link.text}
                                      </Link>
                                  )}
                                </Typography>
                              </AccordionDetails>
                            </Accordion>
                        ))}
                      </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        );

      default:
        return null;
    }
  };

  const renderBlock = (block, idx) => {

    // OLD FORMAT SUPPORT (Paper Submission)
    if (!block.type && block.content) {
      return (
          <Box key={idx} mt={3}>
            {block.title && (
                <Typography variant='subtitle1' fontWeight='bold'>
                  {block.title}
                </Typography>
            )}

            {block.content.map((text, i) => (
                <Typography key={i} mt={1}>
                  {text}
                </Typography>
            ))}

            {block.button && (
                <Button
                    href={block.button.href}
                    variant='contained'
                    color={block.button.color || 'primary'}
                    disabled={block.button.disabled || false}
                    sx={{ mt: 2 }}
                    target='_blank'
                    rel='noopener noreferrer'
                >
                  {block.button.label}
                </Button>
            )}

            {(block.footer || block.link) && (
                <Typography mt={2}>
                  {block.footer}{' '}
                  {block.link && (
                      <Link
                          href={block.link.href}
                          target='_blank'
                          color='primary'
                          sx={{
                            textDecoration: 'underline',
                            '&:hover': { color: 'secondary.main' },
                          }}
                      >
                        {block.link.text}
                      </Link>
                  )}
                </Typography>
            )}
          </Box>
      );
    }

    // NEW FORMAT SUPPORT (Poster Guidelines)
    switch (block.type) {
      case 'text':
        return (
            <Typography key={idx} mt={1}>
              {block.text}
            </Typography>
        );

      case 'list':
        return (
            <Box key={idx} mt={2}>
              {block.title && (
                  <Typography variant='subtitle1' fontWeight='bold'>
                    {block.title}
                  </Typography>
              )}
              <List sx={{ paddingLeft: 2 }}>
                {block.items.map((item, i) => renderListItem(item, i))}
              </List>
            </Box>
        );

      case 'button':
        return (
            <Button
                key={idx}
                href={block.href}
                variant='contained'
                color={block.color || 'primary'}
                sx={{ mt: 2 }}
                target='_blank'
            >
              {block.label}
            </Button>
        );

      case 'link':
        return (
            <Typography key={idx} mt={2}>
              <Link
                  href={block.href}
                  target='_blank'
                  color='primary'
                  sx={{
                    textDecoration: 'underline',
                    '&:hover': { color: 'secondary.main' },
                  }}
              >
                {block.text}
              </Link>
            </Typography>
        );

      default:
        return null;
    }
  };

  return (
    <Container id='guidelines' maxWidth='lg' sx={{ py: 5 }}>
      <CustomHeader value='Guidelines' sx={{ mb: 3 }} />
      <Grid container spacing={2} gap={'20px'} padding={'20px'}>
        {PAGE_CONTENT.map((section, index) => (
          <Grid item xs={12} key={index}>
            {/* <Accordion sx={{ backgroundColor: 'white', borderRadius: 5 }}> */}
              {/* <AccordionSummary expandIcon={<ExpandMoreIcon />}> */}
                <Typography variant='h5' sx={{ fontWeight: 'bold', color: '#333' }}>
                  {section.title}
                </Typography>
              {/* </AccordionSummary> */}
              <AccordionDetails>
                {section.intro && (
                  <Typography variant='body1' color='textSecondary' paragraph>
                    {section.intro}
                  </Typography>
                )}
                {renderSectionContent(section)}
              </AccordionDetails>
            {/* </Accordion> */}
          </Grid>
        ))}
      </Grid>
    </Container>
  );
};

export default Guidelines;